import { useMemo, useRef, useEffect } from 'react';
import { palette, shadow, admin } from '../../styles/tokens';
import { AppointmentBlock } from './AppointmentBlock';
import type { BookingRow } from './hooks';

const TIME_COL_W = 60;
const MIN_COL_W = 140;
const SLOT_H = 48; // height per 15-min slot

interface Resource {
  id: string;
  name: string;
  assigned_user_id: string | null;
  assigned_name?: string;
  color?: string | null;
}

interface BusinessHours {
  open_time: string; // "09:00"
  close_time: string; // "17:00"
}

interface Props {
  bookings: BookingRow[];
  resources: Resource[];
  businessHours: BusinessHours;
  date: string;
  slotInterval?: number; // minutes, default 15
  onSlotClick: (resourceId: string, time: string) => void;
  onBookingClick: (booking: BookingRow) => void;
}

function timeToMinutes(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
}

function fmtTime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const dh = h % 12 || 12;
  return m === 0 ? `${dh} ${ampm}` : `${dh}:${String(m).padStart(2, '0')} ${ampm}`;
}

export function AppointmentGrid({ bookings, resources, businessHours, date, slotInterval = 15, onSlotClick, onBookingClick }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const today = new Date().toISOString().split('T')[0];
  const isToday = date === today;

  const openMin = timeToMinutes(businessHours.open_time);
  const closeMin = timeToMinutes(businessHours.close_time);
  const totalSlots = Math.ceil((closeMin - openMin) / slotInterval);
  const gridHeight = totalSlots * SLOT_H;

  // Column width: distribute evenly, min 140
  const colW = Math.max(MIN_COL_W, resources.length > 0 ? Math.floor(800 / resources.length) : 200);

  // Time labels
  const timeLabels = useMemo(() => {
    const labels: { label: string; min: number; isHour: boolean }[] = [];
    for (let m = openMin; m < closeMin; m += slotInterval) {
      labels.push({ label: fmtTime(m), min: m, isHour: m % 60 === 0 });
    }
    return labels;
  }, [openMin, closeMin, slotInterval]);

  // Group bookings by resource
  const bookingsByResource = useMemo(() => {
    const map = new Map<string, BookingRow[]>();
    for (const r of resources) map.set(r.id, []);
    for (const b of bookings) {
      if (b.date !== date || b.status === 'cancelled') continue;
      // Match to resource by assigned_id → resource.assigned_user_id
      const res = resources.find(r => r.assigned_user_id === b.assigned_id);
      if (res) {
        map.get(res.id)?.push(b);
      } else if (resources.length > 0) {
        // Unassigned → first column
        map.get(resources[0].id)?.push(b);
      }
    }
    return map;
  }, [bookings, resources, date]);

  // NOW indicator position
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const nowTop = isToday && nowMin >= openMin && nowMin <= closeMin
    ? ((nowMin - openMin) / slotInterval) * SLOT_H : -1;

  // Auto-scroll to now
  useEffect(() => {
    if (isToday && scrollRef.current && nowTop > 0) {
      scrollRef.current.scrollTop = Math.max(0, nowTop - 150);
    }
  }, [isToday, nowTop]);

  if (resources.length === 0) {
    return (
      <div style={{ padding: '60px 20px', textAlign: 'center', background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}` }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: palette.g700, marginBottom: 6 }}>No chairs configured</div>
        <div style={{ fontSize: 13.5, color: palette.g500 }}>Set up chairs and assign operators to see the appointment grid.</div>
      </div>
    );
  }

  return (
    <div style={{ background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, boxShadow: shadow.card, overflow: 'hidden' }}>
      {/* Column headers */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${admin.border}`, position: 'sticky', top: 0, zIndex: 5, background: palette.white }}>
        <div style={{ width: TIME_COL_W, minWidth: TIME_COL_W, padding: '10px 8px', fontSize: 11, color: palette.g500, fontWeight: 500 }}>Time</div>
        {resources.map(r => (
          <div key={r.id} style={{ width: colW, minWidth: MIN_COL_W, padding: '10px 8px', borderLeft: `1px solid ${admin.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: r.assigned_user_id ? palette.accentWash : palette.g100, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: r.assigned_user_id ? palette.accentDeep : palette.g400 }}>
              {(r.assigned_name ?? r.name).split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: palette.g800 }}>{r.assigned_name ?? 'Open'}</div>
              <div style={{ fontSize: 11, color: palette.g500 }}>{r.name}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Grid body */}
      <div ref={scrollRef} style={{ maxHeight: 600, overflowY: 'auto', position: 'relative' }}>
        <div style={{ display: 'flex', position: 'relative', height: gridHeight }}>
          {/* Time labels */}
          <div style={{ width: TIME_COL_W, minWidth: TIME_COL_W, position: 'relative' }}>
            {timeLabels.map((t, i) => (
              <div key={i} style={{ position: 'absolute', top: i * SLOT_H, height: SLOT_H, width: '100%', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', paddingRight: 8, paddingTop: 2 }}>
                {t.isHour && <span style={{ fontSize: 11, color: palette.g500, fontWeight: 500 }}>{t.label}</span>}
              </div>
            ))}
          </div>

          {/* Columns */}
          {resources.map(r => {
            const colBookings = bookingsByResource.get(r.id) ?? [];
            return (
              <div
                key={r.id}
                style={{ width: colW, minWidth: MIN_COL_W, position: 'relative', borderLeft: `1px solid ${admin.border}`, height: gridHeight }}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const y = e.clientY - rect.top + (scrollRef.current?.scrollTop ?? 0);
                  const slotIndex = Math.floor(y / SLOT_H);
                  const min = openMin + slotIndex * slotInterval;
                  const h = Math.floor(min / 60);
                  const m = min % 60;
                  onSlotClick(r.assigned_user_id ?? r.id, `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
                }}
              >
                {/* Grid lines */}
                {timeLabels.map((t, i) => (
                  <div key={i} style={{
                    position: 'absolute', top: i * SLOT_H, left: 0, right: 0,
                    height: 0, borderTop: t.isHour ? `0.5px solid ${palette.g200}` : `0.5px dashed ${palette.g150}`,
                  }} />
                ))}

                {/* Booking blocks */}
                {colBookings.map(b => {
                  const startMin = timeToMinutes(b.start_time);
                  const endMin = timeToMinutes(b.end_time);
                  const top = ((startMin - openMin) / slotInterval) * SLOT_H;
                  const height = ((endMin - startMin) / slotInterval) * SLOT_H;
                  return (
                    <AppointmentBlock
                      key={b.id}
                      booking={b}
                      top={top}
                      height={Math.max(height, SLOT_H * 0.8)}
                      onClick={() => onBookingClick(b)}
                    />
                  );
                })}
              </div>
            );
          })}

          {/* NOW line */}
          {nowTop > 0 && (
            <div style={{ position: 'absolute', top: nowTop, left: 0, right: 0, height: 2, background: palette.red, zIndex: 10, pointerEvents: 'none' }}>
              <span style={{ position: 'absolute', left: 4, top: -8, fontSize: 9, fontWeight: 700, color: palette.red, background: palette.white, padding: '0 3px', borderRadius: 3 }}>NOW</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
